import moment from 'moment-timezone';

export const MEXICO_TIMEZONE = 'America/Mexico_City';

const WALL_CLOCK_FORMAT = 'YYYY-MM-DD HH:mm';

/**
 * Converts API wall-clock date/time (America/Mexico_City) to a UTC ISO string.
 */
export function mexicoWallClockToIso(
  date: string,
  time: string,
  timeZone: string = MEXICO_TIMEZONE,
): string | undefined {
  if (!date?.trim() || !time?.trim()) {
    return undefined;
  }

  const parsed = moment.tz(
    `${date.trim()} ${time.trim()}`,
    WALL_CLOCK_FORMAT,
    timeZone || MEXICO_TIMEZONE,
  );

  if (!parsed.isValid()) {
    return undefined;
  }

  return parsed.toISOString();
}

function toInstant(value: string | Date): Date | null {
  const instant = value instanceof Date ? value : new Date(value);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

/**
 * Formats a stored UTC instant as wall-clock time in America/Mexico_City.
 */
export function formatMexicoTime(
  value: string | Date | null | undefined,
  localeTag = 'es-MX',
  timeZone: string = MEXICO_TIMEZONE,
): string {
  const instant = value == null ? null : toInstant(value);
  if (!instant) {
    return '';
  }

  return instant.toLocaleTimeString(localeTag, {
    timeZone: timeZone || MEXICO_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Formats a stored UTC instant as calendar date in America/Mexico_City.
 */
export function formatMexicoDate(
  value: string | Date | null | undefined,
  localeTag = 'es-MX',
  timeZone: string = MEXICO_TIMEZONE,
): string {
  const instant = value == null ? null : toInstant(value);
  if (!instant) {
    return '';
  }

  return instant.toLocaleDateString(localeTag, {
    timeZone: timeZone || MEXICO_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

/** Detail view: DD/MM/YYYY HH:mm - HH:mm (Mexico). */
export function formatMexicoCollectionScheduleDetail(
  start: string | Date,
  end: string | Date,
  localeTag = 'es-MX',
  timeZone: string = MEXICO_TIMEZONE,
): string {
  return `${formatMexicoDate(start, localeTag, timeZone)} ${formatMexicoTime(start, localeTag, timeZone)} - ${formatMexicoTime(end, localeTag, timeZone)}`;
}

/** List view: HH:mm - HH:mm DD/MM/YYYY (Mexico). */
export function formatMexicoCollectionScheduleList(
  start: string | Date,
  end: string | Date,
  localeTag = 'es-MX',
  timeZone: string = MEXICO_TIMEZONE,
): string {
  return `${formatMexicoTime(start, localeTag, timeZone)} - ${formatMexicoTime(end, localeTag, timeZone)} ${formatMexicoDate(start, localeTag, timeZone)}`;
}
